import { Modal } from "react-bootstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { bouncy } from "ldrs";
import {
  parseTimeForInput,
  useSlaCategoryStore,
} from "./SlaCategorystore/useSlaCategoryStore";

bouncy.register();

const SLA_CATEGORY_NAME_PATTERN = /^[a-zA-Z0-9\s\-]+$/;

const cancelButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #1e40af",
  background: "#ffffff",
  color: "#1e40af",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
};

const saveButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #0f172a",
  background: "#0f172a",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  minWidth: 72,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const AddEditSlaCategoryForm = ({
  showModal,
  setShowModal,
  editRow,
  isEditMode,
}) => {
  const {
    isSaving,
    saveSlaCategory,
  } = useSlaCategoryStore();

  const handleClose = () => {
    setShowModal(false);
  };

  const initialValues = {
    categoryName: isEditMode ? editRow?.categoryName || "" : "",
    durationInHr: isEditMode ? editRow?.durationInHr?.toString() || "" : "",
    startTime: isEditMode ? parseTimeForInput(editRow?.startTime) : "",
    endTime: isEditMode ? parseTimeForInput(editRow?.endTime) : "",
    isActive: isEditMode
      ? editRow?.isActive === false
        ? "false"
        : "true"
      : "true",
  };

  const validationSchema = Yup.object().shape({
    categoryName: Yup.string()
      .trim()
      .required("SLA Category Name is required")
      .min(3, "Minimum 3 characters required.")
      .matches(
        SLA_CATEGORY_NAME_PATTERN,
        "Only letters, numbers, spaces, and hyphens are allowed."
      ),
    durationInHr: Yup.number()
      .typeError("Duration must be a valid number")
      .required("Duration (In Hr) is required")
      .positive("Duration must be greater than 0")
      .max(24, "Maximum 24 hours allowed"),
    startTime: Yup.string().required("Start Time is required"),
    endTime: Yup.string()
      .required("End Time is required")
      .test(
        "end-after-start",
        "End Time must be after Start Time",
        function (value) {
          const { startTime } = this.parent;
          if (!value || !startTime) return true;
          return value > startTime;
        }
      ),
    isActive: Yup.string().required("Status is required"),
  });

  const handleSubmit = async (values) => {
    const payload = {
      categoryName: values.categoryName.trim(),
      durationInHr: Number(values.durationInHr),
      startTime: values.startTime,
      endTime: values.endTime,
      isActive: values.isActive === "true",
    };

    if (isEditMode) {
      payload.slaCategoryId = editRow.slaCategoryId;
    }

    const result = await saveSlaCategory(payload, isEditMode);

    if (result?.success) {
      handleClose();
    }
  };

  return (
    <Modal
      size="md"
      show={showModal}
      onHide={handleClose}
      aria-labelledby="sla-category-modal-title"
      backdrop="static"
      centered
    >
      <Modal.Header>
        <Modal.Title id="sla-category-modal-title">
          {isEditMode ? "Edit SLA Category" : "Add SLA Category"}
        </Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              handleClose();
            }
          }}
          aria-label="Close modal"
        ></i>
      </Modal.Header>
      <hr className="mt-4" />
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="mb-3">
                <label htmlFor="categoryName" className="form-label">
                  SLA Category Name
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Field
                  type="text"
                  name="categoryName"
                  id="categoryName"
                  placeholder="Enter Category Name"
                  className="form-control"
                  maxLength={100}
                  onChange={(event) => {
                    const formattedValue = event.target.value
                      .slice(0, 100)
                      .replace(/[^a-zA-Z0-9\s\-]/g, "");
                    setFieldValue("categoryName", formattedValue);
                  }}
                />
                <ErrorMessage
                  className="required-message"
                  name="categoryName"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="durationInHr" className="form-label">
                  Duration (In Hr)
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Field
                  type="number"
                  name="durationInHr"
                  id="durationInHr"
                  placeholder="Enter Duration (In Hr)"
                  className="form-control"
                  min="1"
                  max="24"
                  step="any"
                  onChange={(event) => {
                    let value = event.target.value.replace(/[^0-9.]/g, "");

                    const firstDotIndex = value.indexOf(".");
                    if (firstDotIndex !== -1) {
                      value =
                        value.slice(0, firstDotIndex + 1) +
                        value.slice(firstDotIndex + 1).replace(/\./g, "");
                    }

                    const [intPart, decPart] = value.split(".");
                    const limitedIntPart = intPart.slice(0, 2);
                    value =
                      decPart !== undefined
                        ? `${limitedIntPart}.${decPart.slice(0, 2)}`
                        : limitedIntPart;

                    setFieldValue("durationInHr", value);
                  }}
                />
                <ErrorMessage
                  className="required-message"
                  name="durationInHr"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="startTime" className="form-label">
                  Start Time
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Field
                  type="time"
                  name="startTime"
                  id="startTime"
                  className="form-control"
                />
                <ErrorMessage
                  className="required-message"
                  name="startTime"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="endTime" className="form-label">
                  End Time
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Field
                  type="time"
                  name="endTime"
                  id="endTime"
                  className="form-control"
                />
                <ErrorMessage
                  className="required-message"
                  name="endTime"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Status
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <div className="d-flex align-items-center gap-4">
                  <div className="form-check mb-0">
                    <Field
                      type="radio"
                      className="form-check-input"
                      name="isActive"
                      id="slaCategoryActive"
                      value="true"
                    />
                    <label className="form-check-label" htmlFor="slaCategoryActive">
                      Active
                    </label>
                  </div>
                  <div className="form-check mb-0">
                    <Field
                      type="radio"
                      className="form-check-input"
                      name="isActive"
                      id="slaCategoryInactive"
                      value="false"
                    />
                    <label className="form-check-label" htmlFor="slaCategoryInactive">
                      Inactive
                    </label>
                  </div>
                </div>
                <ErrorMessage
                  className="required-message"
                  name="isActive"
                  component="div"
                />
              </div>

              <hr />
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  style={cancelButtonStyle}
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button type="submit" style={saveButtonStyle} disabled={isSaving}>
                  {isSaving ? (
                    <l-bouncy size="22" speed="1.75" color="white"></l-bouncy>
                  ) : isEditMode ? (
                    <>
                      Update
                    </>
                  ) : (
                    <>
                      Save
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditSlaCategoryForm;
